import {
  BaseResponseProps,
  DetailItem,
  HeaderSection,
  IAppTableColumn,
} from '@qlibs/react-components';
import { CompanyProperties, initialCompany } from './types/company.type';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate, useParams } from 'react-router-dom';
import { backToList } from '../../helpers/listFilterQuery';
import useDetailBreadcrumbs from '../../hooks/useDetailBreadcrumbs';
import React from 'react';
import { httpRequest } from '../../helpers/api';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Grid,
  Image,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  message,
} from 'antd';
import SectionContent from '../../components/SectionContent';
import { generateAddress } from '../../helpers/address';
import { ColumnsType } from 'antd/lib/table';
import { CustomFormItemPer1 } from '../user/components/CustomFormItem';
import { isRamus2CompanyId } from '../../helpers/ramus2';
import {
  CompanyMobileActionButton,
  CompanyMobileAlert,
  CompanyMobileDataTable,
  CompanyMobileDetailLabel,
  CompanyMobileDetailRow,
  CompanyMobileDetailRows,
  CompanyMobileDetailSection,
  CompanyMobileDetailSectionHeader,
  CompanyMobileDetailValue,
  CompanyMobileImagePreview,
  CompanyMobileNoImagePreview,
  CompanyMobilePage,
  MobileCompanyHeader,
} from './mobile';

const moment = require('moment');

const getCompanyFlagImageSrc = (value?: string | null) => {
  const normalizedValue = value?.trim();

  if (!normalizedValue) return null;

  const lowerCaseValue = normalizedValue.toLowerCase();
  if (
    lowerCaseValue.includes('/images/empty-image.png') ||
    lowerCaseValue.includes('/images/flag-not-found.png')
  ) {
    return null;
  }

  return normalizedValue;
};

interface ILocation {
  companyId: string;
}

interface ResponseProps extends BaseResponseProps<
  Omit<CompanyProperties, 'createdAt' | 'updatedAt' | 'companies'>
> {}

const getLatestFile = (e: any) => {
  return [e?.fileList.pop()];
};

const CompanyDetail = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { setBreadcrumbDetails } = useDetailBreadcrumbs();
  const { companyId } = useParams<keyof ILocation>() as ILocation;
  const isReadOnlyRamus2 = isRamus2CompanyId(companyId);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<CompanyProperties>({
    ...initialCompany,
  });
  const [previewImage, setPreviewImage] = React.useState<any>(null);
  const [isPreviewImageError, setIsPreviewImageError] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);

        const res = await httpRequest.get<ResponseProps>(
          '/company/' + companyId,
        );
        if (!res) {
          message.error('Something went wrong');
          return;
        }

        setData({
          ...res.data.payload,
        });

        setPreviewImage(res.data.payload.imageLink || null);
        setIsPreviewImageError(false);

        let label = `Detail Company`;
        const bcDetails = [
          {
            field: 'companyId',
            value: companyId,
            label,
          },
        ];
        setBreadcrumbDetails(bcDetails);

        setIsLoading(false);
      } catch (error: any) {
        console.error(error);
        setIsLoading(false);
      }
    };

    fetchUser();

    // eslint-disable-next-line
  }, [companyId]);

  const handleClickEdit = () => {
    navigate('/company/' + companyId + '/edit');
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Image',
      dataIndex: 'imageLink',
      key: 'imageLink',
      width: 70,
      render: (value: string, _: any) => (
        <Image
          width={60}
          height={60}
          style={{
            objectFit: 'contain',
            border: 'thin solid #ddd',
            overflow: 'hidden',
            background: '#dadada',
            borderRadius: 4,
          }}
          src={value}
          fallback="/images/flag-not-found.png"
        />
      ),
    },
    {
      title: 'Asset Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Asset Type',
      dataIndex: 'assetType',
      key: 'assetType',
      render: (value: string, _: any) => (
        <DetailItem label="" value={value.toUpperCase()} />
      ),
    },
    {
      title: 'Asset Category',
      dataIndex: 'assetCategory',
      key: 'assetCategory',
      render: (value: string, _: any) => (
        <DetailItem label="" value={value.toUpperCase()} />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: string, _: any) => (
        <DetailItem
          label=""
          value={value.toUpperCase()}
          type="tag"
          _tagProps={{
            color: value === 'active' ? 'success' : 'error',
          }}
        />
      ),
    },
  ];

  const mobileAssetColumns: ColumnsType<any> = [
    {
      title: 'Image',
      dataIndex: 'imageLink',
      key: 'imageLink',
      width: 70,
      render: (value: string) => (
        <Image
          width={40}
          height={40}
          style={{
            objectFit: 'contain',
            border: 'thin solid #ddd',
            overflow: 'hidden',
            background: '#dadada',
            borderRadius: 4,
          }}
          src={value}
          fallback="/images/flag-not-found.png"
        />
      ),
    },
    {
      title: 'Asset Name',
      dataIndex: 'name',
      key: 'name',
      width: 230,
    },
    {
      title: 'Asset Type',
      dataIndex: 'assetType',
      key: 'assetType',
      width: 150,
      render: (value: string) => value?.toUpperCase() || 'N/A',
    },
    {
      title: 'Asset Category',
      dataIndex: 'assetCategory',
      key: 'assetCategory',
      width: 190,
      render: (value: string) => value?.toUpperCase() || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: string) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value === 'active' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
  ];

  const renderMobileDetailRow = (
    label: string,
    value: React.ReactNode,
    options?: { fullWidth?: boolean },
  ) => (
    <CompanyMobileDetailRow $fullWidth={options?.fullWidth}>
      <CompanyMobileDetailLabel>{label}</CompanyMobileDetailLabel>
      <CompanyMobileDetailValue>{value || 'N/A'}</CompanyMobileDetailValue>
    </CompanyMobileDetailRow>
  );

  const companyFlagImageSrc = getCompanyFlagImageSrc(previewImage);
  const shouldShowCompanyFlagImage =
    Boolean(companyFlagImageSrc) && !isPreviewImageError;

  if (isMobile) {
    return (
      <CompanyMobilePage>
        <MobileCompanyHeader
          title="Company"
          onBack={() => backToList(navigate, '/company')}
          action={
            isReadOnlyRamus2 ? null : (
              <CompanyMobileActionButton
                disabled={data.deletedAt ? true : false}
                type="primary"
                onClick={handleClickEdit}
              >
                Edit
              </CompanyMobileActionButton>
            )
          }
        />

        <Spin spinning={isLoading}>
          {data?.deletedAt ? (
            <CompanyMobileAlert>
              <Alert
                type="warning"
                message="This data has been archived and cannot be edited"
              />
            </CompanyMobileAlert>
          ) : (
            false
          )}

          <CompanyMobileDetailSection $bordered>
            <CompanyMobileDetailSectionHeader>
              <h2>Information</h2>
              <p>Detail information about company</p>
            </CompanyMobileDetailSectionHeader>
            <CompanyMobileDetailRows>
              <CompanyMobileDetailRow $fullWidth>
                <CompanyMobileDetailLabel>
                  Company Flag
                </CompanyMobileDetailLabel>
                <CompanyMobileDetailValue>
                  {shouldShowCompanyFlagImage ? (
                    <CompanyMobileImagePreview
                      key={companyFlagImageSrc}
                      src={companyFlagImageSrc as string}
                      preview={false}
                      $height={300}
                      onError={() => setIsPreviewImageError(true)}
                    />
                  ) : (
                    <CompanyMobileNoImagePreview height={300} />
                  )}
                </CompanyMobileDetailValue>
              </CompanyMobileDetailRow>
              {renderMobileDetailRow('Name', data.name)}
              {renderMobileDetailRow('Group', data.group?.name || 'N/A')}
              {renderMobileDetailRow('PIC Name', data.picName || 'N/A')}
              {renderMobileDetailRow(
                'PIC Phone Number',
                data.picPhoneNumber || 'N/A',
              )}
              {renderMobileDetailRow(
                'Created by',
                data?.userCreator?.name || 'N/A',
              )}
              {renderMobileDetailRow(
                'Created at',
                data.createdAt
                  ? moment(data.createdAt).format('DD MMMM YYYY, HH:mm')
                  : 'N/A',
              )}
              {renderMobileDetailRow(
                'Updated at',
                data.updatedAt
                  ? moment(data.updatedAt).format('DD MMMM YYYY, HH:mm')
                  : 'N/A',
              )}
              {renderMobileDetailRow(
                'Status',
                <Tag color={data.status === 'active' ? 'success' : 'error'}>
                  {data.status === 'active' ? 'Active' : 'Inactive'}
                </Tag>,
              )}
            </CompanyMobileDetailRows>
          </CompanyMobileDetailSection>

          <CompanyMobileDetailSection $bordered>
            <CompanyMobileDetailSectionHeader>
              <h2>Address</h2>
              <p>These are company information, you can change anything</p>
            </CompanyMobileDetailSectionHeader>
            <CompanyMobileDetailRows>
              {renderMobileDetailRow('Address', generateAddress(data.address), {
                fullWidth: true,
              })}
            </CompanyMobileDetailRows>
          </CompanyMobileDetailSection>

          <CompanyMobileDetailSection $bordered>
            <CompanyMobileDetailSectionHeader>
              <h2>Features</h2>
              <p>These are feature information, you can change anything</p>
            </CompanyMobileDetailSectionHeader>
            <CompanyMobileDetailRows>
              {renderMobileDetailRow(
                'Fuel Monitoring System',
                <Tag color={data?.isFms ? 'success' : 'error'}>
                  {data?.isFms ? 'Active' : 'Inactive'}
                </Tag>,
              )}
              {renderMobileDetailRow(
                'Bunkering Monitoring',
                <Tag color={data?.isBm ? 'success' : 'error'}>
                  {data?.isBm ? 'Active' : 'Inactive'}
                </Tag>,
              )}
              {renderMobileDetailRow(
                'Asset Monitoring',
                <Tag color={data?.isAm ? 'success' : 'error'}>
                  {data?.isAm ? 'Active' : 'Inactive'}
                </Tag>,
              )}
              {renderMobileDetailRow(
                'Tank Level Monitoring',
                <Tag color={data?.isTlm ? 'success' : 'error'}>
                  {data?.isTlm ? 'Active' : 'Inactive'}
                </Tag>,
              )}
            </CompanyMobileDetailRows>
          </CompanyMobileDetailSection>

          <CompanyMobileDetailSection>
            <CompanyMobileDetailSectionHeader>
              <h2>Assets</h2>
              <p>These are company information, you can change anything</p>
            </CompanyMobileDetailSectionHeader>
            <CompanyMobileDataTable
              columns={mobileAssetColumns}
              dataSource={data.assets}
              pagination={false}
              size="small"
              rowKey="assetId"
              scroll={{ x: 760 }}
            />
          </CompanyMobileDetailSection>
        </Spin>
      </CompanyMobilePage>
    );
  }

  return (
    <React.Fragment>
      <HeaderSection
        icon="back"
        title={'Detail Company'}
        subtitle={`Manage your company data`}
        rightAction={
          isReadOnlyRamus2 ? (
            false
          ) : (
            <Space>
              <Button
                disabled={data.deletedAt ? true : false}
                type="primary"
                onClick={handleClickEdit}
              >
                Edit Company
              </Button>
            </Space>
          )
        }
      />

      <Spin spinning={isLoading}>
        <Card>
          {data?.deletedAt ? (
            <Alert
              className="mb-5"
              type="warning"
              message="This data has been archived and cannot be edited"
            />
          ) : (
            false
          )}
          <SectionContent
            groupTitle="Information"
            subTitle="Detail information about company"
          >
            <Row>
              <Col span={24}>
                <p style={{ color: 'rgb(118, 132, 153)' }}>Company Flag</p>
                <Image
                  fallback="/images/empty-image.png"
                  src={previewImage as any}
                  width={'100%'}
                  height={300}
                  style={{
                    objectFit: 'contain',
                    backgroundColor: '#f4f6f9',
                    borderRadius: 8,
                    marginBottom: 40,
                    display: 'block',
                    marginTop: -5,
                  }}
                />
              </Col>
              <Col span={12}>
                <DetailItem label="Name" value={data.name} />
              </Col>
              <Col span={12}>
                <DetailItem label="Group" value={data.group?.name || 'N/A'} />
              </Col>
              <Col span={12}>
                <DetailItem label="PIC Name" value={data.picName || 'N/A'} />
              </Col>
              <Col span={12}>
                <DetailItem
                  label="PIC Phone Number"
                  value={data.picPhoneNumber || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label="Created by"
                  value={data?.userCreator?.name || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label="Created at"
                  value={moment(data.createdAt).format('DD MMMM YYYY, HH:mm')}
                  // type='datetime'
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label="Updated at"
                  value={moment(data.updatedAt).format('DD MMMM YYYY, HH:mm')}
                  // type='datetime'
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label="Status"
                  value={data?.status?.toUpperCase()}
                  type="tag"
                  _tagProps={{
                    color: data.status === 'active' ? 'success' : 'error',
                  }}
                />
              </Col>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle="Address"
            subTitle="These are company information, you can change anything"
          >
            <Row>
              <DetailItem
                label="Address"
                value={generateAddress(data.address)}
              />
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle="Features"
            subTitle="These are feature information, you can change anything"
          >
            <Row>
              <Col span={6}>
                <DetailItem
                  label="Fuel Monitoring System"
                  value={data?.isFms ? 'Active' : 'Inactive'}
                  type="tag"
                  _tagProps={{
                    color: data?.isFms ? 'success' : 'error',
                  }}
                />
              </Col>
              <Col span={6}>
                <DetailItem
                  label="Bunkering Monitoring"
                  value={data?.isBm ? 'Active' : 'Inactive'}
                  type="tag"
                  _tagProps={{
                    color: data?.isBm ? 'success' : 'error',
                  }}
                />
              </Col>
              <Col span={6}>
                <DetailItem
                  label="Asset Monitoring"
                  value={data?.isAm ? 'Active' : 'Inactive'}
                  type="tag"
                  _tagProps={{
                    color: data?.isAm ? 'success' : 'error',
                  }}
                />
              </Col>
              <Col span={6}>
                <DetailItem
                  label="Tank Level Monitoring"
                  value={data?.isTlm ? 'Active' : 'Inactive'}
                  type="tag"
                  _tagProps={{
                    color: data?.isTlm ? 'success' : 'error',
                  }}
                />
              </Col>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle="Assets"
            subTitle="These are company information, you can change anything"
          >
            <Table
              columns={columns}
              dataSource={data.assets}
              pagination={false}
            />
          </SectionContent>
        </Card>
      </Spin>
    </React.Fragment>
  );
};

export default CompanyDetail;
